"use client";

import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { Box } from "@motorata/base-ui.components.atoms._atoms";
import { LabelH2, LabelH3, P } from "@motorata/base-ui.components.atoms.typography";
import { useForm } from "react-hook-form";
import Select from "@motorata/base-ui.components.atoms.select";
import {
  ASSISTANT_ARRAY,
  COLOR_TYPES_ARRAY,
  COLORS_ARRAY, COMFORT_ARRAY,
  COUNTRY_ARRAY, MULTIMEDIA_ARRAY, SECURITY_ARRAY
} from "@motorata/base-ui.data";
import Input from "@motorata/base-ui.components.atoms.input";
import Button from "@motorata/base-ui.components.atoms.button";
import CheckboxesGroup from "@motorata/base-ui.components.atoms.checkboxes-group";
import { SentryService } from "@motorata/base-ui.services.sentry-service";
import { useRouter } from "next/navigation";
import { OfferType } from "@motorata/base-ui.types.api";
import { UserService } from "../../services/UserService";
import AddOfferGallery from "@/views/account/components/AddOfferGallery";

const AccountEditOffer = ({ offer, handleSubmit : handleSubmitSuccess }: {
  offer: OfferType
  handleSubmit: () => void
}) => {
  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm({
    defaultValues: {
      type: offer.type,
      state: offer.state,
      course: offer.course,
      color: offer.params?.color,
      colorType: offer.params?.colorType,
      country: offer.params?.country,
      vin: offer.params?.vin,
      price: offer.finance?.price,
      youtube: offer.youtube,
      description: offer.description,
    }
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const [equipmentState, setEquipmentState] = useState("security");
  const [equipment, setEquipment] = useState(offer.equipment);
  const [invoice, setInvoice] = useState(offer.finance.invoice);
  const [images, setImages] = useState<any[]>(offer.images);

  const type = watch("type");
  const state = watch("state");
  const course = watch("course");
  const color = watch("color");
  const colorType = watch("colorType");
  const country = watch("country");
  const price = watch("price");
  const description = watch("description");
  const youtube = watch("youtube");

  const onSubmit = useCallback(async (el) => {
    try {
      setLoading(true);
      let body = {
        state: el.state,
        course: el.course && el.course !== "" ? el.course : "0",
        isAvailable: true,
        isDamaged: false,
        params: {
          color: el.color,
          colorType: el.colorType,
          vin: el.vin,
          country: el.country
        },
        finance: {
          price: el.price,
          type: "gross",
          invoice: invoice
        },
        images: images?.filter(el => !el.isUploading).map(el => el.id),
        equipment: equipment,
        description: el.description
      };

      await UserService.editOffer(offer.id, body);
      handleSubmitSuccess();
    } catch (e) {
      alert("Nie udało się edytować ogłoszenia. Proszę spróbować później.");
      SentryService.error("[ERROR UserService.editOffer API]", e);
    } finally {
      setLoading(false);
    }
  }, [invoice, equipment, images]);

  return (
      <Wrapper>
        <Content>
          <Box marginBottom="S">
            <LabelH2 fontSize="XL" margin="0">Dodatkowe informacje</LabelH2>
            <P small>Podaj dodatkowe informacje o pojeździe</P>
          </Box>
          <Box marginBottom="S">
            <Grid>
              <Select
                name="state"
                label="Stan pojazdu*"
                options={[
                  ["Nowy", "brandNew"],
                  ["Używany", "used"]
                ]}
                value={state}
                error={errors.state}
                rhf={register("state", { required: true })}
              />
              <Input
                name="course"
                elementType="numeric"
                decimalScale={0}
                value={course}
                label="Przebieg*"
                endAdornment="km"
                error={errors.course}
                rhf={register("course", { required: true })}
              />
            </Grid>
          </Box>
          <Box marginBottom="S">
            <LabelH3>Wygląd zewnętrzny</LabelH3>
            <Grid marginTop="XS">
              <Select
                name="color"
                label="Kolor*"
                options={COLORS_ARRAY}
                value={color}
                error={errors.color}
                rhf={register("color", { required: true })}
              />
              <Select
                name="colorType"
                label="Typ koloru*"
                options={COLOR_TYPES_ARRAY}
                value={colorType}
                error={errors.colorType}
                rhf={register("colorType", { required: true })}
              />
            </Grid>
          </Box>
          <Box>
            <LabelH3>Inne</LabelH3>
            <Grid marginTop="XS">
              <Select
                name="country"
                label="Kraj pochodzenia*"
                options={COUNTRY_ARRAY}
                error={errors.country}
                value={watch("country")}
                rhf={register("country", { required: true })}
              />
              <Input name="vin" label="VIN" error={errors.vin} rhf={register("vin", { required: false })} />
            </Grid>
          </Box>
        </Content>
        <Content>
          <Box marginBottom="S">
            <LabelH2 fontSize="XL" margin="0">Cena pojazdu</LabelH2>
            <P small>Edytuj cene pojazdu</P>
          </Box>
          <Grid mobile="1fr" gtc="1fr 1fr">
            <Input
              name="price"
              elementType="numeric"
              decimalScale={0}
              value={price}
              label="Cena (brutto)*"
              endAdornment="zł"
              error={errors.price}
              margin="0"
              rhf={register("price", { required: true })}
            />
          </Grid>
          <Box marginTop="S">
            <CheckboxesGroup
              name="invoice"
              value={[invoice]}
              onChange={({ value }) => setInvoice(value[value.length - 1] ?? "none")}
              options={[
                ["Brak faktury", "none"],
                ["Faktura VAT", "vat"],
                ["Faktura VAT marża", "vat_discount"]
              ]}
            />
          </Box>
        </Content>
        {
          type !== "motorbikes" &&
          <Content>
            <Box marginBottom="S">
              <LabelH2 fontSize="XL" margin="0">Wyposażenie pojazdu</LabelH2>
              <P small>Edytuj wyposażenie swojego pojazdu</P>
            </Box>
            <ButtonsWrapper>
              <Button
                type={equipmentState === "security" ? "primary" : "secondary"}
                onClick={() => setEquipmentState("security")}
                width="max-content"
              >
                Bezpieczeństwo
              </Button>
              <Button
                type={equipmentState === "comfort" ? "primary" : "secondary"}
                onClick={() => setEquipmentState("comfort")}
                width="max-content"
              >
                Komfort
              </Button>
              <Button
                type={equipmentState === "assistant" ? "primary" : "secondary"}
                onClick={() => setEquipmentState("assistant")}
                width="max-content"
              >
                Systemy wspomagające
              </Button>
              <Button
                type={equipmentState === "audio" ? "primary" : "secondary"}
                onClick={() => setEquipmentState("audio")}
                width="max-content"
              >
                Audio i multimedia
              </Button>
            </ButtonsWrapper>
            <EquipmentContent>
              <CheckboxesGroup
                name="states"
                value={equipment}
                onChange={({ value }) => setEquipment(value)}
                options={equipmentState === "security" ? SECURITY_ARRAY : equipmentState === "comfort" ? COMFORT_ARRAY : equipmentState === "assistant" ? ASSISTANT_ARRAY : MULTIMEDIA_ARRAY}
                marginBottom="S"
              />
            </EquipmentContent>
          </Content>
        }
        <Content>
          <Box marginBottom="S">
            <LabelH2 fontSize="XL" margin="0">Zdjęcia</LabelH2>
            <P small>Edytuj zdjęcia swojego pojazdu</P>
          </Box>
          <AddOfferGallery images={images} setImages={setImages} />
          <Input
            name="youtube"
            value={youtube}
            label="Link do YouTube*"
            error={errors.youtube}
            margin="0"
            rhf={register("youtube", {
              required: false,
              pattern: {
                value: /^http(?:s?):\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)([\w\-_]*)(&(amp;)?[\w?=]*)?/,
                message: "Niepoprawny format linku"
              }
            })}
            marginTop="S"
          />
        </Content>
        <Content>
          <Box marginBottom="S">
            <LabelH2 fontSize="XL" margin="0">Opis oferty</LabelH2>
            <P small>Dodaj opis oferty z dodatkowymi informacjami</P>
          </Box>
          <Box>
            <Input
              name="description"
              label="Opis"
              value={description}
              error={errors.description}
              margin="0"
              rhf={register("description")}
              multiline
              rows={12}
            />
          </Box>
        </Content>
        <SubmitWrapper>
          <Button width="100%" loading={loading} onClick={handleSubmit(onSubmit)}>
            Edytuj ogłoszenie
          </Button>
        </SubmitWrapper>
      </Wrapper>
  );
};

export default AccountEditOffer;

const EquipmentContent = styled(Box)`
  >div>div {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    
    >button{
      label {
        text-align: left;
      }
    }
    
    @media (max-width: 800px){
      grid-template-columns: 1fr 1fr;
    }
  }
  `;

const ButtonsWrapper = styled(Box)`
  display: flex;
  grid-gap: ${({ theme }) => theme.space.XS};
  margin-bottom: ${({ theme }) => theme.space.S};
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    > * {
      width: 100%;
    }
  }
`;

const Grid = styled(Box)<{
  mobile?: string
}>`
  display: grid;
  grid-template-columns: ${({ gtc = "1fr 1fr 1fr 1fr" }) => gtc};
  grid-gap: ${({ theme }) => theme.space.XS};
  grid-template-columns: 1fr 1fr 1fr;
}

@media (max-width: 600px) {
  grid-template-columns: ${({ mobile = "1fr 1fr" }) => mobile};
}
`;

const Content = styled(Box)`
  border-bottom: 1px solid ${({ theme }) => theme.color.fourth};
  padding: ${({ theme }) => theme.space.M} 0;
`;

const Wrapper = styled(Box)`
  > div:first-of-type {
    padding-top: 0;
  }
`;

const SubmitWrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.M} ${({ theme }) => theme.space.S};
`;
